import { TERRAIN } from '../config';
import { moistField } from './climate';

// Shared procedural height field. Everything that touches the ground (collision floor, node and
// grass placement, the displaced ground mesh, foundation bases) samples terrainHeight so they
// agree. While TERRAIN.amplitude is 0 the world is flat and every consumer degenerates to its
// old y=0 behavior; flipping amplitude turns the world wild without new code paths.

function hash(ix: number, iz: number): number {
  let h = (ix * 374761393 + iz * 668265263 + TERRAIN.seed * 69069) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function valueNoise(x: number, z: number): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const u = smooth(x - xi);
  const w = smooth(z - zi);
  const a = hash(xi, zi);
  const b = hash(xi + 1, zi);
  const c = hash(xi, zi + 1);
  const d = hash(xi + 1, zi + 1);
  return lerp(lerp(a, b, u), lerp(c, d, u), w);
}

// Octaves of value noise: large-wavelength rolling that reads under a fixed iso camera. Octave count
// + gain + lacunarity are live-tunable (TERRAIN.fbm*), so the admin panel can reshape the noise.
function fbm(x: number, z: number): number {
  let amp = 1;
  let freq = 1;
  let sum = 0;
  let norm = 0;
  const oct = TERRAIN.fbmOctaves, gain = TERRAIN.fbmGain, lac = TERRAIN.fbmLacunarity;
  for (let o = 0; o < oct; o++) {
    sum += amp * valueNoise(x * freq, z * freq);
    norm += amp;
    amp *= gain;
    freq *= lac;
  }
  return sum / norm;
}

// Ridged noise: sharp crests instead of smooth bumps -- this is what makes cliffs and jagged peaks.
function ridged(x: number, z: number, oct: number, seed: number): number {
  let amp = 1, freq = 1, sum = 0, norm = 0;
  for (let o = 0; o < oct; o++) {
    const n = valueNoise(x * freq + seed, z * freq + seed * 1.3);
    const r = 1 - Math.abs(2 * n - 1);
    sum += amp * r * r;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function terrainHeight(x: number, z: number): number {
  const f = 1 / TERRAIN.cell;
  // Mild domain warp so features are organic, not grid-aligned.
  const wx = (fbm((x + 130) * f * 0.6, (z - 70) * f * 0.6) - 0.5) * TERRAIN.warp;
  const wz = (fbm((x - 90) * f * 0.6, (z + 110) * f * 0.6) - 0.5) * TERRAIN.warp;
  const px = x + wx, pz = z + wz;
  const d = Math.hypot(x, z); // distance from the island centre (drives the coastal lowlands + the edge sink)
  // Regional roughness: a low-frequency mask makes some areas near-flat plains and others jagged,
  // so the map is not uniformly bumpy.
  const rough = fbm(x * f * TERRAIN.roughFreq + 600, z * f * TERRAIN.roughFreq - 500);
  const roughM = TERRAIN.flatFloor + (1 - TERRAIN.flatFloor) * smoothstep(0.35, 0.7, rough);
  // Gentle rolling base everywhere, flattened in the plains regions.
  let base = (fbm(px * f, pz * f) * 2 - 1) * TERRAIN.amplitude * roughM;
  // Arid flatten: dry (desert/scrub) regions are mostly flat with only gentle swells, so they read as
  // open buildable basins. Moisture is the SAME equalized lane the biome paint uses (engine/climate.ts),
  // so the flat ground lines up with where the Desert biome paints. Mountains (peaks below) are untouched.
  const moist = moistField(x, z);
  const arid = clamp01((0.52 - moist) * 2.2); // 0 wet .. 1 bone dry (wider, gentler ramp = more flat desert)
  base *= 1 - 0.45 * arid; // dry lowlands keep MORE rolling relief (volatile badlands, not pancake) -- still open enough to build
  // Mountains placed by NOISE across the interior (DE-CENTERED): a few BIG climbable ranges PLUS many smaller
  // scattered peaks, faded off the beach but NOT biased toward the map centre (no inland^2), so the tallest
  // peak lands wherever the mask crests -- off-centre and different per seed.
  const inland = smooth(clamp01((TERRAIN.islandInner - d) / TERRAIN.coastRamp));
  const coastFade = smoothstep(0, TERRAIN.mtnCoastBand, inland); // gentle band off the beach, NOT inland^2 (no centre bias)
  base *= TERRAIN.coastBaseFloor + (1 - TERRAIN.coastBaseFloor) * inland; // rolling lowlands near the coast
  // Shared peak SHAPE: mostly a rounded dome with a touch of ridge crest (gentle, climbable flanks).
  const ridge = ridged(px * f * 0.75, pz * f * 0.75, TERRAIN.ridgedOctaves, 17);
  const dome = fbm(px * f * 0.40 + 21, pz * f * 0.40 - 13);
  const peakShape = dome * 0.78 + ridge * 0.22;
  // BIG RANGES: a low-frequency mask picks a few large climbable massifs scattered across the interior.
  const mBig = fbm(x * f * TERRAIN.bigMtnFreq + 50, z * f * TERRAIN.bigMtnFreq - 40);
  const bigCover = smoothstep(TERRAIN.mountainThresh, TERRAIN.mountainThresh + 0.42, mBig); // wide ramp = flared, walkable flanks
  // SMALL SCATTERED PEAKS: a higher-frequency mask on its own lane sprinkles many smaller knolls between the
  // ranges, so the interior is not just a few blobs.
  const mSmall = fbm(x * f * TERRAIN.smallMtnFreq + 320, z * f * TERRAIN.smallMtnFreq - 260);
  const smallCover = smoothstep(TERRAIN.smallMountainThresh, TERRAIN.smallMountainThresh + 0.30, mSmall);
  let h = base
        + peakShape * TERRAIN.peakAmp * bigCover * coastFade
        + peakShape * TERRAIN.peakAmpSmall * smallCover * coastFade;
  const mtnCover = Math.max(bigCover, smallCover); // proxy for the detail/mound gates below

  // PATCHY 4-15m MICRO-RELIEF: a mid-frequency mound layer modulated by a low-frequency ruggedness field so
  // SOME regions are bumpy and OTHERS stay flat meadows (patchy contrast, not uniform). Gentle enough to walk
  // + build (peak slope ~3*moundAmp*moundFreq/cell stays under MOVE.slopeEasy). Suppressed inside mountain
  // cores and dry basins, faded off the coast so the beach stays flat.
  if (TERRAIN.moundAmp > 0) {
    const rug = fbm(x * f * TERRAIN.ruggedFreq - 410, z * f * TERRAIN.ruggedFreq + 360);
    const rugM = smoothstep(TERRAIN.ruggedThresh, TERRAIN.ruggedThresh + 0.25, rug); // 0 flat meadow .. 1 bumpy region
    const mf = f * TERRAIN.moundFreq;
    const mound = fbm(px * mf + 77, pz * mf - 53) * 2 - 1; // ~[-1,1] mid-frequency mounds on their own lane
    h += mound * TERRAIN.moundAmp * rugM * inland * (1 - 0.2 * arid) * (1 - 0.8 * mtnCover); // deserts KEEP the bumpy mounds now (walkable-capped, so still buildable) -> volatile dunes/badland relief
  }

  // Secondary high-frequency DETAIL: fine bumps + mini-cliffs on top of the base shape, varied per region, so
  // cells read more complex and vegetation has texture to sit in. Gated to stay gentle on the coast (inland)
  // and on flat plains/deserts ((1 - arid)), and to crag up on rough/rocky + mountain ground (roughM, mtnCover)
  // -- WITHOUT reintroducing the over-steep mountain-flank cliffs (amplitude is only a few metres). Modulated
  // by fields already computed above, NOT a biomeAt() call (terrain.ts must not import biome.ts).
  if (TERRAIN.detailAmp > 0) {
    const df = f * TERRAIN.detailFreq;
    const fine = valueNoise(px * df + 911, pz * df - 733) * 2 - 1; // ~[-1,1] fine octave on its own lane
    const crag = roughM * (1 + 0.8 * mtnCover); // more detail on rugged ground + inside mountain cores
    h += fine * TERRAIN.detailAmp * (inland * crag * (1 - 0.85 * arid));
  }

  // JAGGED ROCK: a high-frequency RIDGED layer (sharp crests, not smooth bumps) that ONLY bites inside mountain
  // cores -- gated by mtnCover^2 so lowlands + the lower flanks stay smooth + walkable while the upper peaks
  // read craggy and rocky instead of a bald dome. This is what turns the smooth mountains ragged.
  const rockAmp = (TERRAIN as { rockAmp?: number }).rockAmp ?? 0;
  if (rockAmp > 0 && mtnCover > 0.04) {
    const rf = f * ((TERRAIN as { rockFreq?: number }).rockFreq ?? 2.4);
    const rk = ridged(px * rf + 411, pz * rf - 233, (TERRAIN as { rockOctaves?: number }).rockOctaves ?? 4, 41) * 2 - 1; // [-1,1] sharp ridges
    h += rk * rockAmp * mtnCover * mtnCover * coastFade; // ^2 concentrates the crag on the true peaks, faded off the coast
  }

  // SPARSE ROCKY OUTCROPS / mini-mesas: a rare, sharper local rise scattered by a HIGH-threshold mask, so the
  // map has occasional dramatic rocky knolls you route around or build on top of. A narrow smoothstep keeps the
  // footprint small + steep-sided; the ridged shape gives a craggy top. Flanks can exceed slopeEasy locally
  // (the drama) but stay under slopeStall (climbable-but-slow), and they are rare, so the map stays ~90%
  // buildable. The splat paints rock on steep slopes, so these auto-read as rock. Faded off the coast + away
  // from mountain cores so they pepper the lowlands/hills.
  if (TERRAIN.outcropAmp > 0) {
    const oMask = fbm(x * f * TERRAIN.outcropFreq + 1500, z * f * TERRAIN.outcropFreq - 1700);
    const oCover = smoothstep(TERRAIN.outcropThresh, TERRAIN.outcropThresh + 0.10, oMask); // narrow ramp = small, sharp footprint
    if (oCover > 0) {
      const oShape = ridged(px * f * TERRAIN.outcropFreq * 1.2 + 90, pz * f * TERRAIN.outcropFreq * 1.2 - 60, 3, 41);
      h += (0.35 + 0.65 * oShape) * TERRAIN.outcropAmp * oCover * inland * (1 - 0.6 * mtnCover);
    }
  }

  // Rivers: a winding channel (where a noise crosses its midline) carved down toward sea level.
  const rv = Math.abs(fbm(px * f * TERRAIN.riverFreq + 200, pz * f * TERRAIN.riverFreq - 100) - 0.5) * 2;
  const rt = clamp01(rv / (TERRAIN.riverWidth + TERRAIN.bankFalloff)); // 0 at the centerline .. 1 at the bank edge
  h -= TERRAIN.riverDepth * (1 - smoothstep(0, 1, rt)); // smoothstep -> a curved river valley, not a vertical wall
  // (Removed the inland "pit" carve: it punched big circular craters into the land. Inland water now comes
  // from the river valleys + natural lows; the seabed below is handled by the bathymetry layer.)
  // Finite island: sink the terrain below sea level past the shoreline, so the map is a landmass
  // ringed by open water (a Rust-style island), not an endless plain. A low-frequency wobble on the
  // shoreline radius gives organic bays and peninsulas instead of a perfect disc.
  const coast = (fbm((x + 300) * f * 0.12, (z - 220) * f * 0.12) - 0.5) * TERRAIN.coastWobble;
  const islandSpan = Math.max(1, TERRAIN.islandOuter - TERRAIN.islandInner); // guard: never divide by <=0
  const edge = smooth(clamp01((d + coast - TERRAIN.islandInner) / islandSpan));
  h = h * (1 - edge) - edge * TERRAIN.islandDepth;
  // Beaches (ABOVE the waterline only -- unchanged): within a height band above sea level, remap the
  // height with a power curve so the coast is FLAT right at the waterline and slow-rolls up to meet the
  // terrain at the top of the band. p = 1 leaves the coast untouched; higher p = flatter, longer
  // beaches. The curve rejoins the terrain exactly at +beachWidth, so there is no step.
  if (TERRAIN.shoreFlatten > 0 && TERRAIN.beachWidth > 0) {
    const dh = h - TERRAIN.seaLevel;
    if (dh > 0 && dh < TERRAIN.beachWidth) {
      const t = dh / TERRAIN.beachWidth; // 0 at the waterline .. 1 at the top of the beach
      const p = 1 + 3.0 * TERRAIN.shoreFlatten;
      h = TERRAIN.seaLevel + TERRAIN.beachWidth * Math.pow(t, p);
    }
  }
  // Bathymetry (BELOW the waterline): a SEPARATE seabed layer, so the underwater world is not the land
  // noise mirrored into cliffs/pits dropping to bedrock. The seabed depth is the UNION (max) of two
  // smooth, monotone, cliff-free shapes, then eased in from the waterline:
  //   1) a near-shore SHELF: shelfDepth*(1 - e^(-u/shelfScale)). Driven by how far the raw land sits
  //      below the sea (u), it eases away from the beach as a gentle sandy shelf and saturates shallow.
  //      Being a saturating curve, it CRUSHES the land's wild excursions (a 60m land pit and a 20m one
  //      both map to nearly the same shallow shelf depth) -> no underwater cliffs here.
  //   2) an open-ocean FLOOR: floorDepth * edge^floorPow. `edge` is the island falloff (0 at the
  //      shoreline .. 1 in the open-water ring), already computed above and perfectly smooth/radial, so
  //      this deepens GRADUALLY out to a flat ocean floor with NO land noise -> no cliffs at depth.
  // max() of the two stays continuous (both -> 0 at the shore). At u = 0 (exactly the waterline) the
  // target depth is 0, so the seabed returns seaLevel with NO step -> it joins the above-water beach
  // with no kink. easeBand fades the reshaping in over the first metres below water (k = 0 at the shore
  // .. 1 once fully under) so the contact line stays byte-stable and the beach->shelf join is seamless.
  // Just one Math.exp, so it stays cheap for the per-sample collision/grass/water/node callers.
  if (h < TERRAIN.seaLevel) {
    const u = TERRAIN.seaLevel - h; // raw land depth below the surface (> 0 here)
    const shelf = TERRAIN.shelfDepth * (1 - Math.exp(-u / Math.max(1e-3, TERRAIN.shelfScale)));
    const floor = TERRAIN.floorDepth * Math.pow(edge, TERRAIN.floorPow); // edge: 0 at shore .. 1 open sea
    const seabed = TERRAIN.seaLevel - Math.max(shelf, floor);
    const k = smoothstep(0, TERRAIN.easeBand, u); // fade in from the shore so the waterline is pinned
    h = lerp(h, seabed, k);
  }
  // Flatten the spawn disc so the player starts level and early builds stay sane.
  const t = clamp01((d - TERRAIN.flatInner) / (TERRAIN.flatOuter - TERRAIN.flatInner));
  return h * smooth(t);
}

function smoothstep(a: number, b: number, x: number): number {
  return smooth(clamp01((x - a) / (b - a)));
}

// March a ray against the height field and return the first hit (for the build cursor, so
// placement does not depend on the streamed ground meshes). Steps coarsely, then binary-refines
// the interval where the ray drops below the terrain. dir need not be normalized.
export function raycastTerrain(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxDist = 1600,
): { x: number; y: number; z: number } | null {
  const len = Math.hypot(dx, dy, dz) || 1;
  const nx = dx / len, ny = dy / len, nz = dz / len;
  const step = 1.5;
  let tPrev = 0;
  let prevDiff = oy - terrainHeight(ox, oz); // > 0 above ground
  for (let t = step; t <= maxDist; t += step) {
    const diff = (oy + ny * t) - terrainHeight(ox + nx * t, oz + nz * t);
    if (diff <= 0 && prevDiff > 0) {
      // crossing in (tPrev, t): binary refine
      let lo = tPrev, hi = t;
      for (let i = 0; i < 18; i++) {
        const mid = (lo + hi) * 0.5;
        const d = (oy + ny * mid) - terrainHeight(ox + nx * mid, oz + nz * mid);
        if (d <= 0) hi = mid; else lo = mid;
      }
      return { x: ox + nx * hi, y: oy + ny * hi, z: oz + nz * hi };
    }
    prevDiff = diff;
    tPrev = t;
  }
  return null;
}

// Magnitude of the terrain gradient (for optional slope gating).
export function terrainSlope(x: number, z: number, eps = 0.6): number {
  const dx = terrainHeight(x + eps, z) - terrainHeight(x - eps, z);
  const dz = terrainHeight(x, z + eps) - terrainHeight(x, z - eps);
  return Math.hypot(dx, dz) / (2 * eps);
}

// Grass suitability 0..1: only on green ground (flat, low, above the waterline), matching the
// ground splat so grass appears exactly where the ground reads as grass. Steep cliffs and high
// rock/snow return 0; beaches and water return 0.
export function terrainGrassiness(x: number, z: number): number {
  const h = terrainHeight(x, z);
  if (h < TERRAIN.seaLevel + 1.5) return 0;
  const slope = terrainSlope(x, z, 1.2);
  const ny = 1 / Math.sqrt(1 + slope * slope); // flatness: 1 flat, lower = steeper
  let g = smoothstep(0.80, 0.92, ny);
  g *= 1 - smoothstep(TERRAIN.snowStart, TERRAIN.snowEnd, h);
  return g;
}
